/**
 * The strategic save's byte codecs (spec §14; plan E13): standard base64 and the self-describing column codec that
 * `save.ts` stores typed-array columns with. A leaf module, with no imports, so code that only reads a saved column
 * (the charter board's `boardSource`, the Reach debug charter) does not pull the whole save → sim → agents → economy
 * graph into the entry chunk (code budget T3, docs/research/2026-10-16-code-budget.md, waste item 3a).
 * `save.ts` re-exports the public helpers, so its importers are unchanged.
 */

/** Any indexable run of numbers (typed arrays, plain arrays). */
export type NumArray = { [i: number]: number; readonly length: number }

// ---------------------------------------------------------------------------------------------
// Base64 (standard alphabet, padded)
// ---------------------------------------------------------------------------------------------

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
const B64_CODES = new Uint8Array(64)
for (let i = 0; i < 64; i++) B64_CODES[i] = B64.charCodeAt(i)
const B64_VALUE = new Int16Array(128).fill(-1)
for (let i = 0; i < 64; i++) B64_VALUE[B64.charCodeAt(i)] = i
const PAD = 61 // '='

/** Standard, padded base64 of `bytes`. */
export function toBase64(bytes: Uint8Array): string {
  const n = bytes.length
  const out = new Uint8Array(Math.ceil(n / 3) * 4)
  let o = 0
  let i = 0
  for (; i + 2 < n; i += 3) {
    const v = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]
    out[o++] = B64_CODES[v >>> 18]
    out[o++] = B64_CODES[(v >>> 12) & 63]
    out[o++] = B64_CODES[(v >>> 6) & 63]
    out[o++] = B64_CODES[v & 63]
  }
  if (i < n) {
    const b1 = i + 1 < n ? bytes[i + 1] : 0
    const v = (bytes[i] << 16) | (b1 << 8)
    out[o++] = B64_CODES[v >>> 18]
    out[o++] = B64_CODES[(v >>> 12) & 63]
    out[o++] = i + 1 < n ? B64_CODES[(v >>> 6) & 63] : PAD
    out[o] = PAD
  }
  return ASCII.decode(out)
}

const ASCII = new TextDecoder()

/** Decodes standard base64. Throws on a bad length or character. */
export function fromBase64(s: string): Uint8Array {
  if (s.length % 4 !== 0) throw new Error('base64 length is not a multiple of 4')
  let pad = 0
  if (s.length > 0 && s.charCodeAt(s.length - 1) === PAD) pad++
  if (s.length > 1 && s.charCodeAt(s.length - 2) === PAD) pad++
  const n = (s.length / 4) * 3 - pad
  const out = new Uint8Array(n)
  let o = 0
  for (let i = 0; i < s.length; i += 4) {
    let v = 0
    for (let j = 0; j < 4; j++) {
      const c = s.charCodeAt(i + j)
      const d = c === PAD ? 0 : c < 128 ? B64_VALUE[c] : -1
      if (d < 0) throw new Error(`bad base64 character at ${i + j}`)
      v = (v << 6) | d
    }
    if (o < n) out[o++] = (v >>> 16) & 255
    if (o < n) out[o++] = (v >>> 8) & 255
    if (o < n) out[o++] = v & 255
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// The column codec
// ---------------------------------------------------------------------------------------------
// A column blob is [kind u8][n u32 LE] and then:
//   DENSE   VALUES(n)
//   CONST   VALUES(1) (every element equals it)
//   SPARSE  VALUES(1) the default, [nz u32] INDEX(nz, < n), VALUES(nz) the other values
//   DICT    [u u32] VALUES(u) the distinct values, INDEX(n, < u)
//   EXCEPT  [m u32] INDEX(m, < n), VALUES(m): the entries that differ from a predicted column the reader rebuilds
// VALUES(k) is [type u8] and k values of the narrowest exact type: u8, i8, u16, i16, u32, i32, f32, f64, or
// PACK ([min f64][bits u8] then k × bits bits, LSB first) for integers when that is smaller. INDEX(k, < r) is
// k × bitsFor(r − 1) packed bits.

const DT_U8 = 0
const DT_I8 = 1
const DT_U16 = 2
const DT_I16 = 3
const DT_U32 = 4
const DT_I32 = 5
const DT_F32 = 6
const DT_F64 = 7
const DT_PACK = 8
const DT_SIZE = [1, 1, 2, 2, 4, 4, 4, 8]

const KIND_DENSE = 0
const KIND_CONST = 1
const KIND_SPARSE = 2
const KIND_DICT = 3
const KIND_EXCEPT = 4

const HEADER = 5

function same(a: number, b: number): boolean {
  return a === b || (a !== a && b !== b)
}

/** Bits to store `0 … max`. */
function bitsFor(max: number): number {
  let b = 0
  while (b < 53 && 2 ** b <= max) b++
  return b
}

function packedBytes(k: number, bits: number): number {
  return Math.ceil((k * bits) / 8)
}

/** What a value block needs: the plain type, and for integers the packed width. */
interface Scan {
  dt: number
  /** Packed bits per value, −1 when not packable. */
  bits: number
  min: number
}

/** Scans `vals[i]` for `i` in `idx` (or the first `n` when `idx` is null). */
function scan(vals: NumArray, n: number, idx: Int32Array | null): Scan {
  let int = true
  let f32 = true
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  for (let k = 0; k < n; k++) {
    const v = vals[idx === null ? k : idx[k]]
    if (int && !Number.isInteger(v)) int = false
    if (f32 && Math.fround(v) !== v) f32 = false
    if (v < min) min = v
    if (v > max) max = v
  }
  if (n === 0) return { dt: DT_U8, bits: -1, min: 0 }
  let dt = f32 ? DT_F32 : DT_F64
  if (int) {
    if (min >= 0) {
      if (max <= 0xff) dt = DT_U8
      else if (max <= 0xffff) dt = DT_U16
      else if (max <= 0xffffffff) dt = DT_U32
    } else if (min >= -0x80 && max <= 0x7f) dt = DT_I8
    else if (min >= -0x8000 && max <= 0x7fff) dt = DT_I16
    else if (min >= -0x80000000 && max <= 0x7fffffff) dt = DT_I32
  }
  const bits = int && max - min < 2 ** 40 ? bitsFor(max - min) : -1
  return { dt, bits, min }
}

/** Bytes of VALUES(k) with scan `s`. */
function valuesSize(s: Scan, k: number): number {
  const plain = 1 + k * DT_SIZE[s.dt]
  if (s.bits < 0) return plain
  const packed = 1 + 9 + packedBytes(k, s.bits)
  return packed < plain ? packed : plain
}

class ByteWriter {
  readonly bytes: Uint8Array
  private readonly view: DataView
  private p = 0
  private acc = 0
  private accBits = 0
  constructor(size: number) {
    this.bytes = new Uint8Array(size)
    this.view = new DataView(this.bytes.buffer)
  }
  u8(v: number): void {
    this.view.setUint8(this.p, v)
    this.p += 1
  }
  u32(v: number): void {
    this.view.setUint32(this.p, v, true)
    this.p += 4
  }
  num(dt: number, v: number): void {
    const d = this.view
    const p = this.p
    switch (dt) {
      case DT_U8:
        d.setUint8(p, v)
        break
      case DT_I8:
        d.setInt8(p, v)
        break
      case DT_U16:
        d.setUint16(p, v, true)
        break
      case DT_I16:
        d.setInt16(p, v, true)
        break
      case DT_U32:
        d.setUint32(p, v, true)
        break
      case DT_I32:
        d.setInt32(p, v, true)
        break
      case DT_F32:
        d.setFloat32(p, v, true)
        break
      default:
        d.setFloat64(p, v, true)
    }
    this.p = p + DT_SIZE[dt]
  }
  /** Appends the low `bits` bits of `v` (0 ≤ v < 2^bits, bits ≤ 40), LSB first. */
  bitsOut(v: number, bits: number): void {
    if (bits <= 24) {
      this.acc |= v << this.accBits
      this.accBits += bits
      while (this.accBits >= 8) {
        this.u8(this.acc & 255)
        this.acc >>>= 8
        this.accBits -= 8
      }
      return
    }
    let x = v
    let left = bits
    while (left > 0) {
      const room = 8 - this.accBits
      const take = left < room ? left : room
      const unit = 2 ** take
      const part = x % unit
      this.acc += part * 2 ** this.accBits
      this.accBits += take
      x = (x - part) / unit
      left -= take
      if (this.accBits === 8) {
        this.u8(this.acc)
        this.acc = 0
        this.accBits = 0
      }
    }
  }
  /** Flushes a partial byte of packed bits. */
  flushBits(): void {
    if (this.accBits > 0) this.u8(this.acc)
    this.acc = 0
    this.accBits = 0
  }
  /** VALUES(k): `vals[idx[j]]` (or `vals[j]`) with scan `s`, packed when that is smaller. */
  values(vals: NumArray, k: number, idx: Int32Array | null, s: Scan): void {
    const plain = 1 + k * DT_SIZE[s.dt]
    if (s.bits >= 0 && 1 + 9 + packedBytes(k, s.bits) < plain) {
      this.u8(DT_PACK)
      this.num(DT_F64, s.min)
      this.u8(s.bits)
      for (let j = 0; j < k; j++) this.bitsOut(vals[idx === null ? j : idx[j]] - s.min, s.bits)
      this.flushBits()
      return
    }
    this.u8(s.dt)
    for (let j = 0; j < k; j++) this.num(s.dt, vals[idx === null ? j : idx[j]])
  }
  /** INDEX(k, < range). */
  index(idx: NumArray, k: number, range: number): void {
    const bits = bitsFor(range - 1)
    for (let j = 0; j < k; j++) this.bitsOut(idx[j], bits)
    this.flushBits()
  }
}

class ByteReader {
  private readonly bytes: Uint8Array
  private readonly view: DataView
  private p = 0
  private acc = 0
  private accBits = 0
  constructor(bytes: Uint8Array) {
    this.bytes = bytes
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  }
  private need(k: number): void {
    if (this.p + k > this.bytes.length) throw new Error('column blob is truncated')
  }
  u8(): number {
    this.need(1)
    return this.view.getUint8(this.p++)
  }
  u32(): number {
    this.need(4)
    const v = this.view.getUint32(this.p, true)
    this.p += 4
    return v
  }
  num(dt: number): number {
    const size = DT_SIZE[dt]
    this.need(size)
    const d = this.view
    const p = this.p
    this.p = p + size
    switch (dt) {
      case DT_U8:
        return d.getUint8(p)
      case DT_I8:
        return d.getInt8(p)
      case DT_U16:
        return d.getUint16(p, true)
      case DT_I16:
        return d.getInt16(p, true)
      case DT_U32:
        return d.getUint32(p, true)
      case DT_I32:
        return d.getInt32(p, true)
      case DT_F32:
        return d.getFloat32(p, true)
      default:
        return d.getFloat64(p, true)
    }
  }
  bitsIn(bits: number): number {
    if (bits <= 24) {
      while (this.accBits < bits) {
        this.acc |= this.u8() << this.accBits
        this.accBits += 8
      }
      const v = this.acc & ((1 << bits) - 1)
      this.acc >>>= bits
      this.accBits -= bits
      return v
    }
    let v = 0
    let got = 0
    while (got < bits) {
      if (this.accBits === 0) {
        this.acc = this.u8()
        this.accBits = 8
      }
      const take = bits - got < this.accBits ? bits - got : this.accBits
      const unit = 2 ** take
      const part = this.acc % unit
      v += part * 2 ** got
      this.acc = (this.acc - part) / unit
      this.accBits -= take
      got += take
    }
    return v
  }
  endBits(): void {
    this.acc = 0
    this.accBits = 0
  }
  /** VALUES(k) into `out[0..k)`. */
  values(out: Float64Array, k: number): void {
    const dt = this.u8()
    if (dt === DT_PACK) {
      const min = this.num(DT_F64)
      const bits = this.u8()
      if (bits > 40) throw new Error(`packed width ${bits} is too wide`)
      this.need(packedBytes(k, bits))
      for (let j = 0; j < k; j++) out[j] = min + this.bitsIn(bits)
      this.endBits()
      return
    }
    if (dt > DT_F64) throw new Error(`unknown column value type ${dt}`)
    this.need(k * DT_SIZE[dt])
    for (let j = 0; j < k; j++) out[j] = this.num(dt)
  }
  /** INDEX(k, < range), checked. */
  index(k: number, range: number): Float64Array {
    const bits = bitsFor(range - 1)
    this.need(packedBytes(k, bits))
    const out = new Float64Array(k)
    for (let j = 0; j < k; j++) {
      const i = this.bitsIn(bits)
      if (!(i < range)) throw new Error(`index ${i} out of range`)
      out[j] = i
    }
    this.endBits()
    return out
  }
  done(): void {
    if (this.p !== this.bytes.length) throw new Error('column blob has trailing bytes')
  }
}

/** Encodes `vals[0..n)` with the smallest codec kind (EXCEPT only when `pred` is given). */
export function encodeColumn(vals: NumArray, n: number, pred: NumArray | null = null): Uint8Array {
  const all = scan(vals, n, null)
  // Distinct values (ascending) and the most common one: a histogram for narrow integers, else runs of a sorted copy.
  let mode = 0
  let modeCount = -1
  const distinctValues: number[] = []
  if (n > 0 && all.bits >= 0 && all.bits <= 16) {
    const hist = new Int32Array(2 ** all.bits)
    for (let i = 0; i < n; i++) hist[vals[i] - all.min]++
    for (let v = 0; v < hist.length; v++) {
      const c = hist[v]
      if (c === 0) continue
      distinctValues.push(all.min + v)
      if (c > modeCount) {
        mode = all.min + v
        modeCount = c
      }
    }
  } else {
    const sorted = new Float64Array(n)
    for (let i = 0; i < n; i++) sorted[i] = vals[i]
    sorted.sort()
    for (let i = 0; i < n; ) {
      let j = i + 1
      while (j < n && same(sorted[j], sorted[i])) j++
      distinctValues.push(sorted[i])
      if (j - i > modeCount) {
        mode = sorted[i]
        modeCount = j - i
      }
      i = j
    }
  }
  const distinct = distinctValues.length
  const modeScan = scan([mode], 1, null)
  let bestKind = KIND_DENSE
  let bestSize = HEADER + valuesSize(all, n)
  if (n > 0 && distinct === 1) {
    bestKind = KIND_CONST
    bestSize = HEADER + valuesSize(modeScan, 1)
  }
  let nzIdx: Int32Array | null = null
  let nzScan: Scan | null = null
  if (n > 0 && distinct > 1) {
    nzIdx = new Int32Array(n - modeCount)
    let k = 0
    for (let i = 0; i < n; i++) if (!same(vals[i], mode)) nzIdx[k++] = i
    nzScan = scan(vals, nzIdx.length, nzIdx)
    const size = HEADER + valuesSize(modeScan, 1) + 4 + packedBytes(nzIdx.length, bitsFor(n - 1)) + valuesSize(nzScan, nzIdx.length)
    if (size < bestSize) {
      bestKind = KIND_SPARSE
      bestSize = size
    }
  }
  const u = distinct
  let uniq: number[] | null = null
  if (n > 0 && u > 1 && u < n / 2) {
    uniq = distinctValues
    const size = HEADER + 4 + valuesSize(scan(uniq, u, null), u) + packedBytes(n, bitsFor(u - 1))
    if (size < bestSize) {
      bestKind = KIND_DICT
      bestSize = size
    }
  }
  let exIdx: Int32Array | null = null
  let exScan: Scan | null = null
  if (pred !== null) {
    let m = 0
    for (let i = 0; i < n; i++) if (!same(vals[i], pred[i])) m++
    exIdx = new Int32Array(m)
    let k = 0
    for (let i = 0; i < n; i++) if (!same(vals[i], pred[i])) exIdx[k++] = i
    exScan = scan(vals, m, exIdx)
    const size = HEADER + 4 + packedBytes(m, bitsFor(n - 1)) + valuesSize(exScan, m)
    if (size < bestSize) {
      bestKind = KIND_EXCEPT
      bestSize = size
    }
  }

  const out = new ByteWriter(bestSize)
  out.u8(bestKind)
  out.u32(n)
  switch (bestKind) {
    case KIND_CONST:
      out.values([mode], 1, null, modeScan)
      break
    case KIND_SPARSE: {
      const idx = nzIdx as Int32Array
      out.values([mode], 1, null, modeScan)
      out.u32(idx.length)
      out.index(idx, idx.length, n)
      out.values(vals, idx.length, idx, nzScan as Scan)
      break
    }
    case KIND_DICT: {
      const values = uniq as number[]
      const at = new Map<number, number>()
      for (let k = 0; k < values.length; k++) at.set(values[k], k)
      const codes = new Float64Array(n)
      for (let i = 0; i < n; i++) codes[i] = at.get(vals[i]) as number
      out.u32(values.length)
      out.values(values, values.length, null, scan(values, values.length, null))
      out.index(codes, n, values.length)
      break
    }
    case KIND_EXCEPT: {
      const idx = exIdx as Int32Array
      out.u32(idx.length)
      out.index(idx, idx.length, n)
      out.values(vals, idx.length, idx, exScan as Scan)
      break
    }
    default:
      out.values(vals, n, null, all)
  }
  return out.bytes
}

/** The element count of a column blob (without decoding it). */
export function columnCount(bytes: Uint8Array): number {
  if (bytes.length < HEADER) throw new Error('column blob is truncated')
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(1, true)
}

/** Decodes a column blob. An EXCEPT column needs the `pred` column its writer used. */
export function decodeColumn(bytes: Uint8Array, pred: NumArray | null = null): Float64Array {
  const r = new ByteReader(bytes)
  const kind = r.u8()
  const n = r.u32()
  if (n > 0x4000000) throw new Error(`column of ${n} values is too long`)
  const out = new Float64Array(n)
  switch (kind) {
    case KIND_DENSE:
      r.values(out, n)
      break
    case KIND_CONST: {
      const one = new Float64Array(1)
      r.values(one, 1)
      out.fill(one[0])
      break
    }
    case KIND_SPARSE: {
      const one = new Float64Array(1)
      r.values(one, 1)
      out.fill(one[0])
      const nz = r.u32()
      if (nz > n) throw new Error('sparse column has more entries than values')
      const idx = r.index(nz, n)
      const vals = new Float64Array(nz)
      r.values(vals, nz)
      for (let k = 0; k < nz; k++) out[idx[k]] = vals[k]
      break
    }
    case KIND_DICT: {
      const u = r.u32()
      if (u > n) throw new Error('dictionary is larger than its column')
      const uniq = new Float64Array(u)
      r.values(uniq, u)
      const codes = r.index(n, u)
      for (let i = 0; i < n; i++) out[i] = uniq[codes[i]]
      break
    }
    case KIND_EXCEPT: {
      if (pred === null || pred.length < n) throw new Error('column needs its predicted column')
      for (let i = 0; i < n; i++) out[i] = pred[i]
      const m = r.u32()
      if (m > n) throw new Error('exception column has more entries than values')
      const idx = r.index(m, n)
      const vals = new Float64Array(m)
      r.values(vals, m)
      for (let k = 0; k < m; k++) out[idx[k]] = vals[k]
      break
    }
    default:
      throw new Error(`unknown column kind ${kind}`)
  }
  r.done()
  return out
}

/** A bundle of column blobs: [count u32] then per blob [length u32][bytes]. */
export function bundle(blobs: readonly Uint8Array[]): Uint8Array {
  let size = 4
  for (const b of blobs) size += 4 + b.length
  const out = new Uint8Array(size)
  const view = new DataView(out.buffer)
  view.setUint32(0, blobs.length, true)
  let p = 4
  for (const b of blobs) {
    view.setUint32(p, b.length, true)
    out.set(b, p + 4)
    p += 4 + b.length
  }
  return out
}

export function unbundle(bytes: Uint8Array): Uint8Array[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (bytes.length < 4) throw new Error('bundle is truncated')
  const count = view.getUint32(0, true)
  const out: Uint8Array[] = []
  let p = 4
  for (let k = 0; k < count; k++) {
    if (p + 4 > bytes.length) throw new Error('bundle is truncated')
    const len = view.getUint32(p, true)
    if (p + 4 + len > bytes.length) throw new Error('bundle is truncated')
    out.push(bytes.subarray(p + 4, p + 4 + len))
    p += 4 + len
  }
  if (p !== bytes.length) throw new Error('bundle has trailing bytes')
  return out
}
